/**
 * Service for claiming gateways via mDNS beacon detection
 *
 * Implements the mDNS claiming flow:
 * 1. Frontend generates claim code and beacons via mDNS
 * 2. Gateway detects beacon and reports to backend
 * 3. Frontend polls backend to see detected gateways
 * 4. User claims gateway via backend API
 *
 * and the same flow run from a code on an appliance's console instead
 * (`claimGatewayByCode`), where the gateway, not the browser, makes the code.
 */

import axios from 'axios';
import { config } from '../config';

/**
 * Broadcast the claim code over mDNS.
 *
 * There is no browser mDNS API — we get the query out by asking for a name
 * under `.local`, which the OS resolves by multicasting. The request itself is
 * always expected to fail; unclaimed gateways on the LAN are sniffing for the
 * query, not answering it (`gateway/src/actors/mdns_query_listener.rs`).
 *
 * In Chrome 142+ this is a Local Network Access request, so the *first* call
 * may raise a permission prompt. Callers must only reach here when the
 * permission is already granted, or from inside a user gesture — see
 * `useLocalNetworkPermission`.
 */
export async function beaconClaimCode(claimCode: string): Promise<void> {
  try {
    await fetch(`https://claim-${claimCode}.local`, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout(1000),
    });
  } catch {
    // Always fails — the name doesn't resolve. Emitting the query is the point.
    // Note this catch can't distinguish "blocked by permission" from the normal
    // failure, which is why permission state is tracked separately.
  }
}

/**
 * Create a claim session before beaconing
 *
 * This endpoint MUST be called before the frontend starts beaconing.
 * Uses Redis SET NX to atomically ensure only one user can register a claim code.
 *
 * @param claimCode The claim code to register
 * @returns Promise resolving to claim session details
 * @throws Error if claim code is already in use (409 conflict)
 */
export async function createClaimSession(claimCode: string): Promise<ClaimSessionResponse> {
  const backendApi = config.backendApi;

  try {
    const response = await axios.post<ClaimSessionResponse>(
      `${backendApi}/api/gateways/claim/session`,
      { claimCode },
      { withCredentials: true }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      throw new Error('Claim code already in use by another user', { cause: error });
    }
    throw error;
  }
}

/**
 * List available gateways for the user's active claim session
 *
 * @param claimCode The claim code to check
 * @returns Promise resolving to list of available gateways
 */
export async function listAvailableGateways(claimCode: string): Promise<AvailableGatewayInfo[]> {
  const backendApi = config.backendApi;

  try {
    const response = await axios.get<ListAvailableGatewaysResponse>(
      `${backendApi}/api/gateways/claim/available`,
      {
        params: { claimCode },
        withCredentials: true,
      }
    );

    // Always return an array, never undefined (React Query requirement)
    return response.data.gateways;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      // A 404 here is the *session* being gone, not an empty result — the
      // backend answers an existing session with no gateways as `200 []`, and
      // only 404s when `claim:{code}` has expired out of Redis. This used to
      // return `[]`, which reads to the caller as "nothing found yet" and let a
      // panel go on beaconing a code the backend would refuse. Callers need to
      // tell the two apart so they can re-register.
      throw new ClaimSessionExpiredError();
    }
    throw error;
  }
}

/**
 * The claim session behind a request is gone — expired, evicted, or never
 * registered. Recoverable: register the same claim code again.
 */
export class ClaimSessionExpiredError extends Error {
  constructor() {
    super('Claim session expired');
    this.name = 'ClaimSessionExpiredError';
  }
}

/**
 * Claim session response from backend
 */
export interface ClaimSessionResponse {
  claimCode: string;
  expiresAt: string;
}

/**
 * Available gateway info from backend
 */
export interface AvailableGatewayInfo {
  sessionId: string;
  hardwareId: string;
  serial: string;
  softwareVersion: string;
  localIp: string;
  detectedAt: string;
}

/**
 * List available gateways response
 */
interface ListAvailableGatewaysResponse {
  claimCode: string;
  gateways: AvailableGatewayInfo[];
}

interface ClaimGatewayResponse {
  success: boolean;
  message?: string;
}

/**
 * How many characters a code on an appliance's console has (gateway
 * `console_claim_code`). It shows them in groups of four.
 */
export const CLAIM_CODE_LENGTH = 12;

/** A code as typed — any case, with or without the dashes — as the gateway beacons it. */
export function normalizeClaimCode(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** No gateway beaconed the code while we waited for it. */
export class NoGatewayWithCodeError extends Error {
  constructor() {
    super(
      'No gateway is showing that code. Check it against the gateway’s screen — it changes ' +
        'whenever the gateway restarts — and that the screen shows a gateway address: the ' +
        'gateway needs one to reach us.',
    );
    this.name = 'NoGatewayWithCodeError';
  }
}

interface ClaimByCodeOptions {
  signal?: AbortSignal;
  /**
   * The code is on screen from the moment the gateway starts, but it is only
   * beaconed (every 10s) once the gateway has settled its VyOS API key — about
   * 75s on a first start. Someone quick can type it before then.
   */
  waitMs?: number;
  pollMs?: number;
}

/**
 * Claim the gateway whose console shows `code`.
 *
 * The mDNS flow with the code's origin reversed: there the browser makes the
 * code and a gateway on its network hears it; here the gateway makes it, shows
 * it on its console and beacons it itself. So the steps are the scan's — register
 * the code as this user's claim session, wait for a gateway to turn up under it,
 * claim that gateway — and nothing here needs to be on the gateway's network.
 */
export async function claimGatewayByCode(
  code: string,
  name: string,
  regionId: string,
  divisionId: string | undefined,
  { signal, waitMs = 180_000, pollMs = 2_000 }: ClaimByCodeOptions = {},
): Promise<AvailableGatewayInfo> {
  await createClaimSession(code);
  const deadline = Date.now() + waitMs;
  for (;;) {
    signal?.throwIfAborted();
    const found = await listAvailableGateways(code);
    // Twelve characters from 29 make a shared code all but impossible; if it
    // happens anyway, claiming either one would be a guess.
    if (found.length > 1) throw new Error('More than one gateway is showing this code.');
    if (found.length === 1) {
      await claimDetectedGateway(code, found[0].sessionId, name, regionId, divisionId);
      return found[0];
    }
    if (Date.now() >= deadline) throw new NoGatewayWithCodeError();
    await delay(pollMs, signal);
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(id);
        reject(signal.reason as Error);
      },
      { once: true },
    );
  });
}

/**
 * Claim a gateway detected via mDNS beacon
 *
 * @param claimCode The claim code used for discovery
 * @param sessionId The session ID of the gateway to claim
 * @param name The name for the gateway
 * @param regionId The region ID where the gateway will be assigned
 * @param divisionId Optional division ID
 * @returns Promise resolving when claim is submitted
 */
export async function claimDetectedGateway(
  claimCode: string,
  sessionId: string,
  name: string,
  regionId: string,
  divisionId?: string
): Promise<void> {
  const backendApi = config.backendApi;

  const response = await axios.post<ClaimGatewayResponse>(
    `${backendApi}/api/gateways/claim`,
    {
      claimCode,
      sessionId,
      name,
      regionId,
      divisionId: divisionId || null,
    },
    { withCredentials: true }
  );

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to claim gateway');
  }
}

